import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import Groups2RoundedIcon from "@mui/icons-material/Groups2Rounded";
import ResponsiveTable from "./ResponsiveTable";
import CategoryFormDialog from "./CategoryFormDialog";
import TeamFormDialog from "./TeamFormDialog";
import PlayersDialog from "./PlayersDialog";
import { useApp } from "../../../context/AppContext";

export default function CompetitionManager() {
  const navigate = useNavigate();
  const { clearSession } = useApp();
  const [searchParams] = useSearchParams();

  const view = searchParams.get("view") || "categories";

  const [categories, setCategories] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);
  const [playersTeam, setPlayersTeam] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [categoryPage, setCategoryPage] = useState(0);
  const [categoryRowsPerPage, setCategoryRowsPerPage] = useState(5);
  const [teamPage, setTeamPage] = useState(0);
  const [teamRowsPerPage, setTeamRowsPerPage] = useState(5);
  const [selectedCategoryId, setSelectedCategoryId] = useState("all");

  const request = useCallback(
    async (url, options = {}) => {
      const access = localStorage.getItem("access");

      const headers = {
        ...(options.body instanceof FormData
          ? {}
          : { "Content-Type": "application/json" }),
        ...(options.headers || {}),
      };

      if (access) headers.Authorization = `Bearer ${access}`;

      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (response.status === 401) {
        clearSession();
        navigate("/admin-login", { replace: true });
        throw new Error("Tu sesión expiró");
      }

      const contentType = response.headers.get("content-type") || "";
      const data = contentType.includes("application/json")
        ? await response.json()
        : null;

      if (!response.ok) {
        throw new Error(
          data?.error ||
            data?.detail ||
            "Ocurrió un error al procesar la solicitud"
        );
      }

      return data;
    },
    [clearSession, navigate]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const [categoriesData, teamsData] = await Promise.all([
        request("/competition/categories/"),
        request("/competition/teams/"),
      ]);

      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      setTeams(Array.isArray(teamsData) ? teamsData : []);
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setTeamPage(0);
  }, [selectedCategoryId, teams.length]);

  useEffect(() => {
    setCategoryPage(0);
  }, [categories.length]);

  const filteredTeams = useMemo(() => {
    if (selectedCategoryId === "all") return teams;
    return teams.filter(
      (team) => String(team.category_id) === String(selectedCategoryId)
    );
  }, [teams, selectedCategoryId]);

  const openDeleteDialog = (target) => {
    setDeleteTarget(target);
    setConfirmOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setConfirmOpen(false);
    setDeleteTarget(null);
  };

  const categoryColumns = useMemo(
    () => [
      { key: "name", label: "Categoría" },
      {
        key: "active",
        label: "Estado",
        render: (row) => (
          <Typography
            sx={{
              fontWeight: 700,
              color: row.active ? "success.main" : "error.main",
            }}
          >
            {row.active ? "Activa" : "Inactiva"}
          </Typography>
        ),
      },
      {
        key: "actions",
        label: "Acciones",
        render: (row) => (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ width: "100%" }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditRoundedIcon />}
              onClick={() => {
                setEditingCategory(row);
                setCategoryDialogOpen(true);
              }}
            >
              Editar
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<DeleteRoundedIcon />}
              onClick={() =>
                openDeleteDialog({
                  type: "category",
                  id: row.id,
                  name: row.name,
                })
              }
            >
              Eliminar
            </Button>
          </Stack>
        ),
      },
    ],
    []
  );

  const teamColumns = useMemo(
    () => [
      {
        key: "logo",
        label: "Escudo",
        render: (row) => (
          <Avatar
            src={row.logo || ""}
            variant="rounded"
            sx={{
              width: 46,
              height: 46,
              borderRadius: "14px",
              bgcolor: "#fff",
              border: "1px solid rgba(0,0,0,0.08)",
            }}
          />
        ),
      },
      {
        key: "team_photo",
        label: "Foto grupal",
        render: (row) =>
          row.team_photo ? (
            <Box
              component="img"
              src={row.team_photo}
              alt={row.name}
              sx={{
                width: 84,
                height: 46,
                objectFit: "cover",
                borderRadius: "14px",
                display: "block",
                border: "1px solid rgba(0,0,0,0.08)",
                bgcolor: "#fff",
              }}
            />
          ) : (
            <Typography
              sx={{
                fontSize: "0.86rem",
                color: "text.secondary",
                fontWeight: 600,
              }}
            >
              Sin foto
            </Typography>
          ),
      },
      { key: "name", label: "Equipo" },
      { key: "category", label: "Categoría" },
      {
        key: "actions",
        label: "Acciones",
        render: (row) => (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ width: "100%" }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<Groups2RoundedIcon />}
              onClick={() => setPlayersTeam(row)}
            >
              Jugadores
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<EditRoundedIcon />}
              onClick={() => {
                setEditingTeam(row);
                setTeamDialogOpen(true);
              }}
            >
              Editar
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<DeleteRoundedIcon />}
              onClick={() =>
                openDeleteDialog({
                  type: "team",
                  id: row.id,
                  name: row.name,
                })
              }
            >
              Eliminar
            </Button>
          </Stack>
        ),
      },
    ],
    []
  );

  const handleSaveCategory = async (values) => {
    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      if (editingCategory) {
        await request(`/competition/categories/${editingCategory.id}/update/`, {
          method: "PUT",
          body: JSON.stringify(values),
        });

        setMessage({
          type: "success",
          text: "Categoría actualizada correctamente",
        });
      } else {
        await request("/competition/categories/create/", {
          method: "POST",
          body: JSON.stringify(values),
        });

        setMessage({
          type: "success",
          text: "Categoría creada correctamente",
        });
      }

      setCategoryDialogOpen(false);
      setEditingCategory(null);

      await loadData();
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    await request(`/competition/categories/${categoryId}/delete/`, {
      method: "DELETE",
    });

    setMessage({
      type: "success",
      text: "Categoría eliminada correctamente",
    });

    if (selectedCategoryId === String(categoryId)) {
      setSelectedCategoryId("all");
    }
  };

  const handleSaveTeam = async (values) => {
    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const formData = new FormData();

      formData.append("name", values.name);
      formData.append("category_id", values.category_id);

      if (values.logo) formData.append("logo", values.logo);
      if (values.team_photo) formData.append("team_photo", values.team_photo);

      if (editingTeam) {
        await request(`/competition/teams/${editingTeam.id}/update/`, {
          method: "PUT",
          body: formData,
        });

        setMessage({
          type: "success",
          text: "Equipo actualizado correctamente",
        });
      } else {
        await request("/competition/teams/create/", {
          method: "POST",
          body: formData,
        });

        setMessage({
          type: "success",
          text: "Equipo creado correctamente",
        });
      }

      setTeamDialogOpen(false);
      setEditingTeam(null);

      await loadData();
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    await request(`/competition/teams/${teamId}/delete/`, {
      method: "DELETE",
    });

    setMessage({
      type: "success",
      text: "Equipo eliminado correctamente",
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    setMessage({ type: "", text: "" });

    try {
      if (deleteTarget.type === "category") {
        await handleDeleteCategory(deleteTarget.id);
      }

      if (deleteTarget.type === "team") {
        await handleDeleteTeam(deleteTarget.id);
      }

      setConfirmOpen(false);
      setDeleteTarget(null);
      await loadData();
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Card
          elevation={0}
          sx={{
            width: "95%",
            borderRadius: "22px",
            border: "1px solid rgba(140,15,44,0.12)",
          }}
        >
          <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
            <Typography variant="h4" sx={{ mb: 2 }}>
              Gestión de competencia
            </Typography>

            {message.text && (
              <Alert
                severity={message.type === "success" ? "success" : "error"}
                onClose={() => setMessage({ type: "", text: "" })}
                sx={{ mb: 2 }}
              >
                {message.text}
              </Alert>
            )}

            {view === "categories" && (
              <>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.2}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", sm: "center" }}
                  sx={{ mb: 2 }}
                >
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 700,
                      color: "text.primary",
                    }}
                  >
                    Categorías
                  </Typography>

                  <Button
                    variant="contained"
                    startIcon={<AddRoundedIcon />}
                    onClick={() => {
                      setEditingCategory(null);
                      setCategoryDialogOpen(true);
                    }}
                  >
                    Nueva categoría
                  </Button>
                </Stack>

                <ResponsiveTable
                  rows={categories}
                  columns={categoryColumns}
                  page={categoryPage}
                  rowsPerPage={categoryRowsPerPage}
                  onPageChange={(_, nextPage) => setCategoryPage(nextPage)}
                  onRowsPerPageChange={(event) => {
                    setCategoryRowsPerPage(parseInt(event.target.value, 10));
                    setCategoryPage(0);
                  }}
                  getRowId={(row) => row.id}
                  getCardTitle={(row) => row.name}
                  emptyText={
                    loading
                      ? "Cargando categorías..."
                      : "Todavía no has creado categorías"
                  }
                />
              </>
            )}

            {view === "teams" && (
              <>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.2}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", md: "center" }}
                  sx={{ mb: 2 }}
                >
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 700,
                      color: "text.primary",
                    }}
                  >
                    Equipos
                  </Typography>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <TextField
                      select
                      label="Filtrar categoría"
                      value={selectedCategoryId}
                      onChange={(e) => setSelectedCategoryId(e.target.value)}
                      sx={{
                        minWidth: {
                          xs: "100%",
                          sm: 220,
                        },
                      }}
                    >
                      <MenuItem value="all">Todas</MenuItem>

                      {categories.map((category) => (
                        <MenuItem key={category.id} value={String(category.id)}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    <Button
                      variant="contained"
                      startIcon={<AddRoundedIcon />}
                      onClick={() => {
                        setEditingTeam(null);
                        setTeamDialogOpen(true);
                      }}
                      disabled={!categories.length}
                    >
                      Nuevo equipo
                    </Button>
                  </Stack>
                </Stack>

                <ResponsiveTable
                  rows={filteredTeams}
                  columns={teamColumns}
                  page={teamPage}
                  rowsPerPage={teamRowsPerPage}
                  onPageChange={(_, nextPage) => setTeamPage(nextPage)}
                  onRowsPerPageChange={(event) => {
                    setTeamRowsPerPage(parseInt(event.target.value, 10));
                    setTeamPage(0);
                  }}
                  getRowId={(row) => row.id}
                  getCardTitle={(row) => row.name}
                  emptyText={
                    loading
                      ? "Cargando equipos..."
                      : categories.length
                      ? "No hay equipos para esta categoría"
                      : "Primero crea al menos una categoría"
                  }
                />
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      <CategoryFormDialog
        open={categoryDialogOpen}
        onClose={() => {
          setCategoryDialogOpen(false);
          setEditingCategory(null);
        }}
        onSubmit={handleSaveCategory}
        initialData={editingCategory}
        loading={saving}
      />

      <TeamFormDialog
        open={teamDialogOpen}
        onClose={() => {
          setTeamDialogOpen(false);
          setEditingTeam(null);
        }}
        onSubmit={handleSaveTeam}
        initialData={editingTeam}
        loading={saving}
        categories={categories}
      />

      <PlayersDialog
        open={Boolean(playersTeam)}
        onClose={() => setPlayersTeam(null)}
        team={playersTeam}
        teams={teams}
        request={request}
      />

      <Dialog
        open={confirmOpen}
        onClose={closeDeleteDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "18px",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "primary.main",
            color: "#fff",
            fontWeight: 700,
            fontSize: "1.05rem",
            mb: 2
          }}
        >
          Confirmar eliminación
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Typography
            sx={{
              fontSize: "0.98rem",
              color: "text.primary",
              lineHeight: 1.6,
            }}
          >
            {deleteTarget?.type === "category"
              ? `¿Estás seguro de que vas a eliminar la categoría ${deleteTarget?.name}?`
              : `¿Estás seguro de que vas a eliminar el equipo ${deleteTarget?.name}?`}
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2.4,
            gap: 1,
          }}
        >
          <Button onClick={closeDeleteDialog} variant="outlined" disabled={deleting}>
            Cancelar
          </Button>

          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            disabled={deleting}
          >
            {deleting ? "Eliminando..." : "Sí, eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}